import type { AskHelenaVoiceVocabulary } from "shared";

/**
 * Deterministic clean-up of dictated text, run on FINAL text only (the grey
 * tail is shown as heard). Ported from the Helena_voice_agent POC.
 *
 * 1. Ticket numbers: "S T C U B zero nine three…" → STCUB09302600004.
 * 2. Names and terms from the tenant vocabulary snap to their spelling.
 *
 * It can never invent an id: it only rejoins letters and digits that were
 * actually spoken. Returns segments so the UI can mark what it changed.
 */

export interface DictationSegment {
  text: string;
  fixed: boolean;
  /** What was heard, when `fixed`. */
  original?: string;
}

/**
 * Both known ids are prefix + MMDDYY + a 5-digit sequence = 11 digits, while
 * the service's pattern allows 6 or more. An open-ended count would swallow
 * the next spoken number ("…zero four, one more thing"), so the count stays
 * exact until the backend confirms the format (DESIGN §10 B1). The service
 * pattern is still checked on top.
 */
const TICKET_ID_DIGITS = 11;
const TICKET_PREFIX = "st";
const MIN_PREFIX_LETTERS = 2;
const MAX_PREFIX_LETTERS = 5;

/**
 * English spoken names for letters. "oh" is deliberately absent — it is read
 * as a digit, and a letter O spoken as "oh" before the digits start is rare
 * next to "oh" meaning zero.
 */
const LETTER_NAMES: Record<string, string> = {
  ay: "a",
  bee: "b",
  be: "b",
  see: "c",
  sea: "c",
  cee: "c",
  dee: "d",
  ef: "f",
  eff: "f",
  gee: "g",
  aitch: "h",
  haitch: "h",
  eye: "i",
  jay: "j",
  kay: "k",
  el: "l",
  ell: "l",
  em: "m",
  en: "n",
  pee: "p",
  cue: "q",
  queue: "q",
  are: "r",
  ar: "r",
  es: "s",
  ess: "s",
  tee: "t",
  tea: "t",
  you: "u",
  vee: "v",
  ex: "x",
  why: "y",
  zee: "z",
  zed: "z",
};

const DIGIT_WORDS: Record<string, string> = {
  zero: "0",
  oh: "0",
  one: "1",
  two: "2",
  three: "3",
  four: "4",
  five: "5",
  six: "6",
  seven: "7",
  eight: "8",
  nine: "9",
};

/** Only these may sit between the spoken parts of one id. */
const JOINER = /^[\s.,-]*$/;

interface Word {
  start: number;
  end: number;
  word: string;
}

const toWords = (text: string): Word[] =>
  [...text.matchAll(/[\p{L}\p{N}'’]+/gu)].map((match) => ({
    start: match.index ?? 0,
    end: (match.index ?? 0) + match[0].length,
    word: match[0],
  }));

/** What one spoken word contributes to an id, if anything. */
const toIdPiece = (
  word: string,
): { letters: string; digits: string } | null => {
  const lower = word.toLowerCase();
  if (lower in DIGIT_WORDS) return { letters: "", digits: DIGIT_WORDS[lower] };
  if (lower in LETTER_NAMES)
    return { letters: LETTER_NAMES[lower], digits: "" };
  if (/^\d+$/.test(word)) return { letters: "", digits: word };
  if (/^[a-z]$/i.test(word)) return { letters: lower, digits: "" };
  // A multi-letter run only counts when the recogniser wrote it in capitals
  // ("STCUB", "CUB0930") — that is how spelled letters come back. A normal
  // word like "street" never starts an id.
  const run = /^([A-Z]+)(\d*)$/.exec(word);
  if (run) return { letters: run[1].toLowerCase(), digits: run[2] };
  return null;
};

const compileTicketPattern = (pattern: string): RegExp | null => {
  try {
    return new RegExp(`^(?:${pattern})$`);
  } catch {
    return null;
  }
};

/** The id read so far: `null` means this start can't become an id. */
type IdProgress = { letters: string; digits: string } | null;

const isValidSoFar = ({
  letters,
  digits,
}: {
  letters: string;
  digits: string;
}) =>
  letters.startsWith(TICKET_PREFIX.slice(0, letters.length)) &&
  letters.length <= TICKET_PREFIX.length + MAX_PREFIX_LETTERS &&
  digits.length <= TICKET_ID_DIGITS;

/** Adds one spoken word to the id, or rejects the whole attempt. */
const extendId = (
  progress: { letters: string; digits: string },
  word: string,
  isFirst: boolean,
): IdProgress => {
  const piece = toIdPiece(word);
  if (!piece) return null;
  // An id starts with its letters, and no letter follows a digit.
  if (isFirst && !piece.letters) return null;
  if (piece.letters && progress.digits) return null;

  const next = {
    letters: progress.letters + piece.letters,
    digits: progress.digits + piece.digits,
  };
  return isValidSoFar(next) ? next : null;
};

const toCompleteId = (
  { letters, digits }: { letters: string; digits: string },
  servicePattern: RegExp | null,
): string | null => {
  if (letters.length < TICKET_PREFIX.length + MIN_PREFIX_LETTERS) return null;
  const id = `${letters.toUpperCase()}${digits}`;
  return !servicePattern || servicePattern.test(id) ? id : null;
};

/** Tries to read one id starting at `words[from]`; returns its last word index. */
const readTicketId = (
  text: string,
  words: Word[],
  from: number,
  servicePattern: RegExp | null,
): { id: string; lastWord: number } | null => {
  let progress: IdProgress = { letters: "", digits: "" };

  for (let i = from; i < words.length && progress; i++) {
    const gap = i > from ? text.slice(words[i - 1].end, words[i].start) : "";
    if (!JOINER.test(gap)) return null;

    progress = extendId(progress, words[i].word, i === from);
    if (progress?.digits.length === TICKET_ID_DIGITS) {
      const id = toCompleteId(progress, servicePattern);
      return id ? { id, lastWord: i } : null;
    }
  }
  return null;
};

const fixTicketIds = (
  text: string,
  vocabulary: AskHelenaVoiceVocabulary,
): DictationSegment[] => {
  const words = toWords(text);
  const servicePattern = compileTicketPattern(vocabulary.ticketNumberPattern);
  const out: DictationSegment[] = [];
  let cursor = 0;

  for (let i = 0; i < words.length; i++) {
    const match = readTicketId(text, words, i, servicePattern);
    if (!match) continue;

    const start = words[i].start;
    const end = words[match.lastWord].end;
    const original = text.slice(start, end);
    if (start > cursor)
      out.push({ text: text.slice(cursor, start), fixed: false });
    out.push({ text: match.id, fixed: original !== match.id, original });
    cursor = end;
    i = match.lastWord;
  }

  if (cursor < text.length)
    out.push({ text: text.slice(cursor), fixed: false });
  return out;
};

const fold = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/['’]/g, "")
    .toLowerCase();

const levenshtein = (a: string, b: string): number => {
  const row = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    let previous = row[0];
    row[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const current = row[j];
      row[j] = Math.min(
        row[j] + 1,
        row[j - 1] + 1,
        previous + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
      previous = current;
    }
  }
  return row[b.length];
};

/**
 * Allowed spelling distance for a name. Short names must match exactly
 * (ignoring case and accents), or "globe" would become "Globex".
 */
const maxDistance = (length: number): number =>
  length >= 10 ? 2 : length >= 7 ? 1 : 0;

interface VocabularyEntry {
  term: string;
  folded: string;
  allowed: number;
}

/**
 * Buckets entries by word count + first letter, so each window is compared
 * against a handful of candidates instead of the whole tenant vocabulary —
 * an MSP's name list can be long, and this runs on every final update.
 */
const indexVocabulary = (vocabulary: AskHelenaVoiceVocabulary) => {
  const buckets = new Map<string, VocabularyEntry[]>();
  const wordCounts = new Set<number>();

  const add = (term: string, fuzzy: boolean) => {
    const folded = fold(term);
    if (!folded) return;
    const count = term.trim().split(/\s+/).length;
    const key = `${count}:${folded[0]}`;
    wordCounts.add(count);
    const entry = {
      term,
      folded,
      allowed: fuzzy ? maxDistance(folded.length) : 0,
    };
    buckets.set(key, [...(buckets.get(key) ?? []), entry]);
  };

  for (const name of vocabulary.names) add(name, true);
  for (const term of vocabulary.terms) add(term, false);

  // Longest names first, so "Exchange Online" wins over a one-word match.
  return { buckets, wordCounts: [...wordCounts].sort((a, b) => b - a) };
};

type VocabularyIndex = ReturnType<typeof indexVocabulary>;

const matchesEntry = (
  candidate: string,
  { folded, allowed }: VocabularyEntry,
) =>
  candidate === folded ||
  (allowed > 0 &&
    Math.abs(candidate.length - folded.length) <= allowed &&
    levenshtein(candidate, folded) <= allowed);

/** The vocabulary entry spoken at `words[i]`, longest first. */
const findTermAt = (
  words: Word[],
  i: number,
  index: VocabularyIndex,
): { entry: VocabularyEntry; count: number } | null => {
  for (const count of index.wordCounts) {
    if (i + count > words.length) continue;
    const window = words.slice(i, i + count);
    const candidate = fold(window.map((w) => w.word).join(" "));
    const bucket = index.buckets.get(`${count}:${candidate[0]}`);
    const entry = bucket?.find((item) => matchesEntry(candidate, item));
    if (entry) return { entry, count };
  }
  return null;
};

const fixTerms = (text: string, index: VocabularyIndex): DictationSegment[] => {
  const words = toWords(text);
  const out: DictationSegment[] = [];
  let cursor = 0;

  for (let i = 0; i < words.length; i++) {
    const match = findTermAt(words, i, index);
    if (!match) continue;

    const start = words[i].start;
    const end = words[i + match.count - 1].end;
    const original = text.slice(start, end);
    if (original !== match.entry.term) {
      if (start > cursor)
        out.push({ text: text.slice(cursor, start), fixed: false });
      out.push({ text: match.entry.term, fixed: true, original });
      cursor = end;
    }
    i += match.count - 1;
  }

  if (cursor < text.length)
    out.push({ text: text.slice(cursor), fixed: false });
  return out;
};

export const normalizeDictation = (
  text: string,
  vocabulary: AskHelenaVoiceVocabulary | null,
): DictationSegment[] => {
  if (!text) return [];
  if (!vocabulary) return [{ text, fixed: false }];

  const index = indexVocabulary(vocabulary);
  return fixTicketIds(text, vocabulary).flatMap((segment) =>
    segment.fixed ? [segment] : fixTerms(segment.text, index),
  );
};

export const segmentsToText = (segments: DictationSegment[]): string =>
  segments.map((segment) => segment.text).join("");
